import { usePrevious } from '@engoo/react-hooks/usePrevious';
import {
  CallStateActionType,
  CallStateLockInitiator,
  CallStateStudentLockReason,
} from '@engoo/realtime-protocol/callState';
import { Stream } from '@engoo/realtime-protocol/stream';
import { SyncState } from 'app/models/rtc/sync/constants';
import { CallOutcome, createCallOutcome } from 'app/containers/call/CallOutcome';
import { useBookingInterface } from 'app/containers/rtc/BookingContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { BookingState, BookingType } from 'app/models/rtc/booking/constants';
import { LoadingStage, useLoadingState } from 'app/containers/state/LoadingState';
import { isBookingCancelled } from 'app/models/rtc/booking/utils';
import { CallStateDocumentManagerState, CallStateDocumentSpec } from 'app/models/rtc/callState/documentSpec';
import { LockState, LockStatus } from 'app/models/rtc/callState/LockState';
import { CallState } from 'app/models/rtc/callState/reducer';
import { useMemoOne } from 'app/utils/useMemoOne';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { throwingMethods } from 'app/utils/throwingMethods';
import React, { ReactNode, useContext, useEffect, useMemo } from 'react';

interface IProps {
  children: (args: ICallStateContext) => ReactNode;
}

export enum CallStage {
  /**
   * Call state wasn't synced, stage can't be determined
   */
  Unknown = 'unknown',
  /**
   * Call starts as awaiting activation. You need to dispatch an unlock
   * action go to active stage
   */
  AwaitingActivation = 'awaitingActivation',
  /**
   * Indicates that the call is in session.
   */
  Active = 'active',
  /**
   * Indicates that the call is done and will not go to any other state.
   */
  PostLocked = 'postLocked',
}

export interface ICallStateContext extends Pick<
  CallStateDocumentManagerState, 'metadata' | 'syncState'
> {
  readonly stage: CallStage;
  readonly unlock: () => Promise<unknown>;
  readonly lock: () => Promise<unknown>;
  readonly lockTime?: Date;
  /**
   * Used to describe the outcome of the call. There may be a window where the
   * call outcome is null and the stage is PostLocked if the booking needs to be
   * refreshed at the end of the lesson.
   */
  readonly outcome: CallOutcome | null;
}

const getActions = (manager: CallStateDocumentManagerState['manager']) => {
  if (!manager) {
    return throwingMethods([
      'lock',
      'unlock',
    ]);
  }

  const lock = async () => {
    await manager.addLocalActionWithResult({
      type: CallStateActionType.Lock,
      initiator: CallStateLockInitiator.Student,
      lockReason: CallStateStudentLockReason.EndLesson,
    });
  };

  const unlock = async () => {
    await manager.addLocalActionWithResult({
      type: CallStateActionType.Unlock,
    });
  };

  return { lock, unlock };
};

export const defaultCallStateContextValue = {
  metadata: undefined,
  syncState: SyncState.closed,
  stage: CallStage.Unknown,
  outcome: null,
  ...getActions(undefined),
};

export const CallStateContext = React.createContext<ICallStateContext>(
  defaultCallStateContextValue,
);

export const calcStage = (callState?: CallState, bookingState?: BookingState) => {
  // A cancelled booking or a locked call status puts the call in a post locked state.
  if (callState?.lockState.lockStatus === LockStatus.Locked || (bookingState && isBookingCancelled(bookingState))) {
    return CallStage.PostLocked;
  }

  if (
    // If there is otherwise no call state or booking state, then it is unknown what to do with the call.
    !callState || !bookingState ||
    // The desired behavior for user role of Other is unknown at this time.
    (bookingState.bookingType === BookingType.Lesson && bookingState.lessonRole === LessonBookingRole.Other)
  ) {
    return CallStage.Unknown;
  }

  // Any booking state that is not a lesson should be active. Otherwise, rely on the call lock state.
  if (bookingState.bookingType !== BookingType.Lesson || callState.lockState.lockStatus === LockStatus.Unlocked) {
    return CallStage.Active;
  }

  // Fall back to awaiting activation.
  return CallStage.AwaitingActivation;
};

export const CallStateDocumentProvider: React.FC = props => {
  const store = useDocumentManager(Stream.CallState, CallStateDocumentSpec);
  const { manager, documentState: callState, syncState, metadata } = store;

  const bookingState = useBookingState();
  const bookingInterface = useBookingInterface();
  const isSynced = syncState === SyncState.synced;

  const stage = calcStage(isSynced ? callState : undefined, bookingState);

  const actions = useMemoOne(() => getActions(manager), [manager]);

  const lockTime = callState?.lockState.lockTime;
  const outcome = useCallOutcome(
    bookingInterface.refreshBooking,
    stage,
    bookingState ?? null,
    callState?.lockState ?? null,
  );

  const contextValue: ICallStateContext = useMemoOne(
    () => ({
      ...actions,
      syncState,
      metadata,
      stage,
      lockTime,
      outcome,
    }),
    [actions, syncState, metadata, outcome, stage, lockTime],
  );

  return (
    <CallStateContext.Provider value={contextValue}>
      {props.children}
    </CallStateContext.Provider>
  );
};

/**
 * Helper to handle the timing and deriving of the outcome for a call.
 */
const useCallOutcome = (
  refreshBooking: () => Promise<void>,
  callStage: CallStage,
  bookingState: BookingState | null,
  lockState: LockState | null,
): CallOutcome | null => {
  const previousStage = usePrevious(callStage);
  const { clear, load, stage } = useLoadingState({ load: refreshBooking });

  const postLocked = callStage === CallStage.PostLocked;

  // Determines if the booking needs to be polled.
  let refresh: boolean;

  switch (previousStage) {
    // If the call was in any of the stages below and goes into a postlocked state, then refresh the booking.
    case CallStage.AwaitingActivation:
    case CallStage.Active:
      refresh = postLocked;
      break;

    // If the call went from an Unknown state then the user never effectively joined the call.
    // If the call went was in a PostLocked stage then decisions regarding this were already made and can be ignored.
    case CallStage.Unknown:
    case CallStage.PostLocked:
    case undefined:
      refresh = false;
      break;
  }

  useEffect(() => {
    if (refresh) {
      load();
    }
  }, [load, refresh]);

  useEffect(() => {
    // If the call stage is not a postlocked state then clear the loading state.
    if (!postLocked) {
      clear();
    }
  }, [clear, postLocked]);

  // It's important that an outcome is not computed while refreshing the lesson, otherwise
  // there may be pre-emptive rendering when the state is inadequate. This requires two states to be checked:
  // the refresh flag and the loading state stage.
  //
  // On the first call the refresh flag will be true, but the loading state will be idle.
  // On subsequent calls, the refresh flag will be false, but the loading state will be loading.
  //
  // On the other hand, if there is no desire to refresh the state then it just needs to
  // be computed based on the post locked state.
  const returnOutcome = postLocked && !refresh && stage !== LoadingStage.Loading;

  return useMemo(() => {
    if (bookingState !== null && returnOutcome) {
      return createCallOutcome(bookingState, lockState);
    }

    return null;
  }, [bookingState, lockState, returnOutcome]);
};


export const useCallStateContext = () => useContext(CallStateContext);

export const CallStateConsumer = (props: IProps) => {
  const callStateContext = useCallStateContext();

  return (
    <>
      {props.children(callStateContext)}
    </>
  );
};
